import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Form, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';

import { backgroundGrey1, lightGrayBorder } from 'lib/css/colors';

const isValidGLN = gln => {
  if (!gln) return false;

  // Remove any whitespace
  gln = gln.trim();

  // Check if it's exactly 13 digits
  if (!/^\d{13}$/.test(gln)) return false;

  // Check if it's all zeros
  if (/^0{13}$/.test(gln)) return false;

  // Calculate check digit using GS1 algorithm
  const digits = gln.split('').map(Number);
  const sum = digits.slice(0, 12).reduce((acc, digit, i) => {
    return acc + digit * (i % 2 === 0 ? 1 : 3);
  }, 0);
  const checkDigit = (10 - (sum % 10)) % 10;

  return checkDigit === digits[12];
};

function GLNEditor({ initialGLNs, onChange, onCancel }) {
  const [glnEntries, setGlnEntries] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (initialGLNs && Object.keys(initialGLNs).length > 0) {
      const entries = Object.entries(initialGLNs).map(([gln, sub_company_name]) => ({
        gln,
        sub_company_name: sub_company_name || '',
      }));
      setGlnEntries(entries);
    } else {
      setGlnEntries([{ gln: '', sub_company_name: '' }]);
    }
  }, [initialGLNs]);

  const updateParent = entries => {
    const validEntries = entries.filter(entry => entry.gln.trim() !== '');

    const glnsObject = validEntries.reduce((obj, entry) => {
      obj[entry.gln] = entry.sub_company_name || null;
      return obj;
    }, {});

    onChange(glnsObject);
  };

  const addNewGLNEntry = () => {
    const updatedEntries = [...glnEntries, { gln: '', sub_company_name: '' }];
    setGlnEntries(updatedEntries);
  };

  const removeGLNEntry = index => {
    const updatedEntries = [...glnEntries];
    updatedEntries.splice(index, 1);
    setGlnEntries(updatedEntries);
    updateParent(updatedEntries);

    // Remove validation error for this entry
    const updatedErrors = { ...validationErrors };
    delete updatedErrors[index];
    setValidationErrors(updatedErrors);
  };

  const updateGLNEntry = (index, field, value) => {
    const updatedEntries = [...glnEntries];
    updatedEntries[index][field] = value;
    setGlnEntries(updatedEntries);

    // Validate GLN when it's updated
    if (field === 'gln') {
      const updatedErrors = { ...validationErrors };
      if (value.trim() && !isValidGLN(value)) {
        updatedErrors[index] = 'Invalid GLN format';
      } else {
        delete updatedErrors[index];
      }
      setValidationErrors(updatedErrors);
    }

    if (field === 'sub_company_name' || (field === 'gln' && value.trim() !== '')) {
      updateParent(updatedEntries);
    }
  };

  return (
    <div className={css(styles.container)}>
      <h6 className="mb-3">Manufacturer GLNs</h6>
      {glnEntries.map((entry, index) => (
        <Row key={index} className="mb-2">
          <Col xs={5}>
            <Form.Control
              as="input"
              className={css(styles.form)}
              autoFocus={index === 0}
              placeholder="GLN"
              value={entry.gln}
              onChange={e => updateGLNEntry(index, 'gln', e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  onCancel();
                }
              }}
              isInvalid={!!validationErrors[index]}
            />
            {validationErrors[index] && (
              <Form.Control.Feedback type="invalid">
                {validationErrors[index]}
              </Form.Control.Feedback>
            )}
          </Col>
          <Col xs={5}>
            <Form.Control
              as="input"
              className={css(styles.form)}
              placeholder="Sub-company name"
              value={entry.sub_company_name}
              onChange={e => updateGLNEntry(index, 'sub_company_name', e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  onCancel();
                }
              }}
            />
          </Col>
          <Col xs={2}>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => removeGLNEntry(index)}
              disabled={glnEntries.length < 1}
            >
              Remove
            </Button>
          </Col>
        </Row>
      ))}
      <Button variant="outline-primary" size="sm" className="mt-2" onClick={addNewGLNEntry}>
        Add GLN
      </Button>

      <div className="mt-3 d-flex justify-content-end">
        <Button variant="outline-secondary" className="mr-2" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: '10px',
    border: `1px solid ${lightGrayBorder}`,
    borderRadius: '5px',
    backgroundColor: backgroundGrey1,
    width: '100%',
  },
  form: {
    fontSize: '0.9rem',
  },
});

GLNEditor.propTypes = {
  initialGLNs: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

GLNEditor.defaultProps = {
  initialGLNs: {},
};

export default GLNEditor;
